import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { RefreshCw, Calendar, Users, Activity, AlertCircle, Bell, Receipt } from 'lucide-react';
import ScheduleCalendar from '../components/ScheduleCalendar';
import ClientScheduleCard from '../components/ClientScheduleCard';
import { useScheduleData } from '../hooks/useScheduleData';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import RoleBasedNavigation from '../components/RoleBasedNavigation';
import { AtomicLoadingScreen } from '../components/ui/atomic-loader';

const Schedule: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const {
    currentDate,
    scheduleData,
    remindersData,
    getRemindersByDay,
    activeTab,
    setActiveTab,
    loading,
    error,
    updatingStatus,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    updateEventStatus,
    getEventStatus,
    refreshData,
    getSortedClients,
    calendarDays,
    monthStats,
    year,
    month
  } = useScheduleData();

  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());

  // Verificar autenticação
  if (!isAuthenticated || !user) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">Acesso Negado</span>
            </div>
            <p className="text-red-600">Você precisa estar logado para acessar esta página.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Obter clientes ordenados para o dia selecionado (apenas para cobranças)
  const sortedClients = activeTab === 'COBRANÇA' ? getSortedClients(selectedDay) : [];

  // Separar clientes com e sem eventos (apenas para cobranças)
  const clientsWithEvents = activeTab === 'COBRANÇA' ? sortedClients.filter(client => {
    const events = client.events[selectedDay];
    return events && events.length > 0;
  }) : [];

  const clientsWithoutEvents = activeTab === 'COBRANÇA' ? sortedClients.filter(client => {
    const events = client.events[selectedDay];
    return !events || events.length === 0;
  }) : [];

  // Separar clientes com e sem lembretes (apenas para lembretes)
  const clientsWithReminders = activeTab === 'LEMBRETE' ? remindersData.filter(client => 
    client.reminders && client.reminders.length > 0
  ) : [];

  const clientsWithoutReminders = activeTab === 'LEMBRETE' ? remindersData.filter(client => 
    !client.reminders || client.reminders.length === 0
  ) : [];

  const handleDayClick = (day: number) => {
    setSelectedDay(day);
  };

  const handleRefresh = () => {
    refreshData();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <AtomicLoadingScreen 
            message="Carregando cronograma..." 
            className="h-64"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">Erro ao carregar dados</span>
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <RoleBasedNavigation>
      <Layout>
        <div className="container mx-auto p-6 space-y-6">
      {/* Cabeçalho com estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">Total de Clientes</p>
                <p className="text-2xl font-bold text-blue-600">{monthStats.totalClients}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Com Eventos</p>
                <p className="text-2xl font-bold text-green-600">{monthStats.clientsWithEvents}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Sem Eventos</p>
                <p className="text-2xl font-bold text-gray-600">{monthStats.clientsWithoutEvents}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-600">Total de Eventos</p>
                <p className="text-2xl font-bold text-purple-600">{monthStats.totalEvents}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toggle Button para Cobranças/Lembretes */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === 'COBRANÇA' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('COBRANÇA')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                activeTab === 'COBRANÇA'
                  ? 'bg-orange-500 hover:bg-orange-600 text-white font-bold'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Receipt className="w-4 h-4" />
              Cobrança
            </Button>
            
            <Button
              variant={activeTab === 'LEMBRETE' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('LEMBRETE')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                activeTab === 'LEMBRETE'
                  ? 'bg-orange-500 hover:bg-orange-600 text-white font-bold'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Bell className="w-4 h-4" />
              Lembrete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seção do Calendário (apenas para cobranças) */}
      {activeTab === 'COBRANÇA' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ScheduleCalendar
              currentDate={currentDate}
              calendarDays={calendarDays}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              onCurrentMonth={goToCurrentMonth}
              onDayClick={handleDayClick}
              selectedDay={selectedDay}
            />
          </div>

          {/* Seção dos Cards de Clientes */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Clientes - Dia {selectedDay}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-100 text-green-600">
                      {clientsWithEvents.length} com eventos
                    </Badge>
                    <Badge variant="outline" className="bg-gray-100 text-gray-600">
                      {clientsWithoutEvents.length} sem eventos
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {sortedClients.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum cliente encontrado para este mês</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Clientes com eventos primeiro */}
                    {clientsWithEvents.map((clientData) => (
                      <ClientScheduleCard
                        key={`${clientData.client.id}-${selectedDay}`}
                        clientData={clientData}
                        day={selectedDay}
                        currentDate={{ year, month }}
                        onStatusChange={updateEventStatus}
                        updatingStatus={updatingStatus}
                        activeTab={activeTab}
                      />
                    ))}

                    {/* Clientes sem eventos */}
                    {clientsWithoutEvents.map((clientData) => (
                      <ClientScheduleCard
                        key={`${clientData.client.id}-${selectedDay}`}
                        clientData={clientData}
                        day={selectedDay}
                        currentDate={{ year, month }}
                        onStatusChange={updateEventStatus}
                        updatingStatus={updatingStatus}
                        activeTab={activeTab}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Seção de Lembretes com Calendário */}
      {activeTab === 'LEMBRETE' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ScheduleCalendar
              currentDate={currentDate}
              calendarDays={calendarDays}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              onCurrentMonth={goToCurrentMonth}
              onDayClick={handleDayClick}
              selectedDay={selectedDay}
            />
          </div>

          {/* Seção dos Cards de Lembretes do Dia */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Lembretes - Dia {selectedDay}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-orange-100 text-orange-600">
                      {getRemindersByDay(selectedDay).length} lembretes
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {getRemindersByDay(selectedDay).length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum lembrete programado para o dia {selectedDay}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Os lembretes aparecem nos dias de envio (data de vencimento - dias antes)
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getRemindersByDay(selectedDay).map((reminder, index) => (
                      <div
                        key={`reminder-${reminder.reminderRuleId}-${reminder.dueDateId}`}
                        className="p-4 border border-orange-200 rounded-lg bg-orange-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 font-semibold text-sm">
                                  {(reminder.client.trade_name || reminder.client.legal_name).charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {reminder.client.trade_name || reminder.client.legal_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {reminder.client.email}
                                </div>
                              </div>
                            </div>
                            
                            <div className="ml-12 space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Bell className="h-4 w-4 text-orange-500" />
                                <span className="font-medium text-orange-700">
                                  Hoje, dia {selectedDay.toString().padStart(2, '0')}/{month.toString().padStart(2, '0')} terá envio para o vencimento do dia {reminder.dueDay.toString().padStart(2, '0')}/{month.toString().padStart(2, '0')}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-gray-600">
                                <span>📅 {reminder.daysBeforeDue} dia(s) antes</span>
                                <span>🕐 Horário: {reminder.windowStart}</span>
                              </div>
                            </div>
                          </div>
                          
                          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                            LEMBRETE
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Seção de Todos os Lembretes Ativos (para referência) */}
      {activeTab === 'LEMBRETE' && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Todos os Lembretes Ativos
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-100 text-green-600">
                  {clientsWithReminders.length} com lembretes
                </Badge>
                <Badge variant="outline" className="bg-gray-100 text-gray-600">
                  {clientsWithoutReminders.length} sem lembretes
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {remindersData.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum lembrete encontrado</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Clientes com lembretes primeiro */}
                {clientsWithReminders.map((clientData) => (
                  <ClientScheduleCard
                    key={`reminder-${clientData.client.id}`}
                    reminderData={clientData}
                    activeTab={activeTab}
                  />
                ))}

                {/* Clientes sem lembretes */}
                {clientsWithoutReminders.map((clientData) => (
                  <ClientScheduleCard
                    key={`reminder-${clientData.client.id}`}
                    reminderData={clientData}
                    activeTab={activeTab}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informações adicionais */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-600 text-xs font-semibold">i</span>
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-1">Como usar o Cronograma de Envios</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Calendário:</strong> Clique nos dias para ver os clientes com eventos programados</li>
                <li>• <strong>Cards verdes:</strong> Clientes com eventos no dia selecionado</li>
                <li>• <strong>Cards cinzas:</strong> Clientes sem eventos no dia selecionado</li>
                <li>• <strong>Checkboxes:</strong> Marque "Em andamento" e "Finalizado" para acompanhar o progresso</li>
                <li>• <strong>Status:</strong> Os checkboxes agora são persistidos e refletidos ao recarregar a página</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
      </Layout>
    </RoleBasedNavigation>
  );
};

export default Schedule;
